/** @file Team editor modal: name, color, roster table. */
import { h, clear } from "./dom.js";
import { openModal } from "./modal.js";
import { teamDocSchema } from "../persistence/schemas.js";

/** @typedef {import("../types/schemas.js").TeamDoc} TeamDoc */
/** @typedef {import("../types/schemas.js").RosterPlayer} RosterPlayer */

const COLORS = [
  "#1e40af",
  "#b91c1c",
  "#16a34a",
  "#eab308",
  "#ea580c",
  "#7c3aed",
  "#0891b2",
  "#db2777",
];

const POSITIONS = ["", "TW", "LA", "RL", "RM", "RR", "RA", "KM"];

/**
 * @param {{ team: TeamDoc | null }} opts
 * @returns {Promise<Omit<TeamDoc, "id" | "createdAt" | "updatedAt"> | null>}
 */
export function openTeamEditor(opts) {
  const initial = opts.team;
  return openModal((close) => {
    /** @type {RosterPlayer[]} */
    let roster = initial ? initial.roster.map((r) => ({ ...r })) : [];
    let selectedColor = initial?.color ?? COLORS[0];

    const nameInput = /** @type {HTMLInputElement} */ (
      h("input", {
        type: "text",
        value: initial?.name ?? "",
        maxLength: 60,
        required: true,
        class: "hb-auth-input",
      })
    );

    const colorRow = h("div", { class: "flex gap-2 flex-wrap" });
    function renderColors() {
      clear(colorRow);
      for (const c of COLORS) {
        const swatch = h("button", {
          type: "button",
          class: "hb-color",
          "aria-label": `Farbe ${c}`,
        });
        swatch.style.setProperty("--swatch", c);
        if (c === selectedColor) {
          swatch.classList.add("hb-color--active");
        }
        swatch.addEventListener("click", () => {
          selectedColor = c;
          renderColors();
        });
        colorRow.appendChild(swatch);
      }
    }
    renderColors();

    const rosterWrap = h("div", { class: "flex flex-col gap-2" });

    function renderRoster() {
      clear(rosterWrap);
      roster.forEach((player, idx) => {
        const nameI = /** @type {HTMLInputElement} */ (
          h("input", {
            type: "text",
            value: player.firstName,
            placeholder: "Vorname",
            maxLength: 40,
            class: "hb-auth-input",
          })
        );
        nameI.addEventListener("input", () => {
          const p = roster[idx];
          if (p) {
            p.firstName = nameI.value;
          }
        });
        const numI = /** @type {HTMLInputElement} */ (
          h("input", {
            type: "number",
            min: 0,
            max: 99,
            value: String(player.number),
            class: "hb-auth-input",
          })
        );
        numI.style.maxWidth = "80px";
        numI.addEventListener("input", () => {
          const p = roster[idx];
          if (p) {
            p.number = Number(numI.value) || 0;
          }
        });
        const posSel = /** @type {HTMLSelectElement} */ (
          h(
            "select",
            { class: "hb-auth-input" },
            POSITIONS.map((p) =>
              h("option", { value: p, selected: (player.position ?? "") === p ? "true" : null }, [
                p === "" ? "—" : p,
              ]),
            ),
          )
        );
        posSel.value = player.position ?? "";
        posSel.addEventListener("change", () => {
          const val = posSel.value;
          const p = roster[idx];
          if (p) {
            p.position = val ? /** @type {any} */ (val) : undefined;
          }
        });
        const del = h("button", { type: "button", class: "hb-btn min-h-11" }, ["Entfernen"]);
        del.addEventListener("click", () => {
          roster.splice(idx, 1);
          renderRoster();
        });
        rosterWrap.appendChild(
          h("div", { class: "flex gap-2 items-center flex-wrap" }, [nameI, numI, posSel, del]),
        );
      });
    }
    renderRoster();

    const addBtn = h("button", { type: "button", class: "hb-btn min-h-11 mt-2" }, [
      "+ Spieler hinzufügen",
    ]);
    addBtn.addEventListener("click", () => {
      if (roster.length >= 25) {
        return;
      }
      roster.push({ firstName: "", number: roster.length + 1 });
      renderRoster();
    });

    const privacy = h("p", { class: "text-xs text-neutral-400" }, [
      "Privacy-by-Design — im MVP speichern wir bewusst weder Nachname noch Geburtstag.",
    ]);

    const errorEl = h("div", { class: "text-sm text-red-400 min-h-5", role: "alert" });
    const submit = h("button", { type: "submit", class: "hb-btn hb-btn--primary min-h-11" }, [
      "Speichern",
    ]);
    const cancel = h("button", { type: "button", class: "hb-btn min-h-11" }, ["Abbrechen"]);
    cancel.addEventListener("click", () => close(null));

    const form = h("form", { class: "flex flex-col gap-3" }, [
      h("h2", { class: "text-xl font-bold" }, [initial ? "Team bearbeiten" : "Neues Team"]),
      h("label", { class: "flex flex-col gap-1 text-sm" }, [h("span", null, ["Teamname"]), nameInput]),
      h("label", { class: "flex flex-col gap-1 text-sm" }, [h("span", null, ["Farbe"]), colorRow]),
      h("div", { class: "flex flex-col gap-2 mt-2" }, [
        h("span", { class: "text-sm font-medium" }, ["Kader"]),
        rosterWrap,
        addBtn,
        privacy,
      ]),
      errorEl,
      h("div", { class: "flex gap-2 justify-end mt-2" }, [cancel, submit]),
    ]);

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const name = nameInput.value.trim();
      if (!name) {
        errorEl.textContent = "Teamname erforderlich";
        return;
      }
      const cleanRoster = roster.filter((p) => p.firstName.trim() !== "");
      const draft = {
        name,
        color: selectedColor,
        roster: cleanRoster,
      };
      const withMeta = {
        id: initial?.id ?? "_new",
        createdAt: new Date(),
        updatedAt: new Date(),
        ...draft,
      };
      const result = teamDocSchema.safeParse(withMeta);
      if (!result.success) {
        errorEl.textContent = "Bitte Eingaben prüfen";
        return;
      }
      close(draft);
    });

    return form;
  });
}
